/**
 * useTopContractors Hook
 *
 * React Query hook for fetching top-rated contractors
 */

'use client';

import { useQuery } from '@tanstack/react-query';
import { fetchTopContractors } from '../../api/fetchTopContractors';
import type { ContractorCardProps } from '../../ui/ContractorCard';

export interface UseTopContractorsOptions {
  limit?: number;
  useMock?: boolean;
}

export interface UseTopContractorsReturn {
  contractors: ContractorCardProps[];
  loading: boolean;
  error: string | null;
  isRefetching: boolean;
}

/**
 * Hook for fetching top-rated contractors using React Query
 */
export function useTopContractors(
  options: UseTopContractorsOptions = {},
): UseTopContractorsReturn {
  const { limit = 4, useMock } = options;

  const {
    data = [],
    isLoading,
    error,
    isRefetching,
  } = useQuery({
    queryKey: ['contractors', 'top', limit],
    queryFn: () => fetchTopContractors({ limit, useMock: false }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnWindowFocus: false,
    retry: 2,
  });

  return {
    contractors: data,
    loading: isLoading,
    error: error ? (error as Error).message : null,
    isRefetching,
  };
}
